import { APIGet, APIPost, APIPut, APIUrl, AdminType } from "api/protocols";
import { CancelToken } from "axios";


interface IPutStep1EmailCampaign {
  id: number;
  name: string;
  description: string;
}
interface IPutStep2EmailCampaign {
  id: number;
  audience_id: number;

  from_name: string;
  from_address: string;

  subject: string;
  text_preview: string;

  scheduled_type: IEmailCampaign['scheduled_type'];
  scheduled_recurring_type?: IEmailCampaign['scheduled_recurring_type'];
  scheduled_at: string;
}
interface IPutStep3EmailCampaign {
  id: number;
  content: string;
}

interface IPutPublishEmailCampaign {
  id: number;
}

interface IGetAudienceCustomerExport {
  id: number;
}
interface IGetAudienceCustomer {
  id: number;
  campaign_id?: number;
  page: number;
  search?: string;
  filters?: IAudienceFilter[];
  has_filters?: boolean;
  imported_customer_ids?: number[];
  removed_customer_ids?: number[];
}

interface IPostAudience {
  title: string;
  customer_ids: number[];
  filters: IAudienceFilter[];
}
interface IPutAudience {
  id: number;
  title: string;
  imported_customer_ids?: number[];
  removed_customer_ids?: number[];
  filters: IAudienceFilter[];
}

export interface IEmailCampaign {
  id: number;
  name: string; // TODO: Decide on name or title
  type: string;
  description: string;
  status: 'published' | 'pending' | 'scheduled' | 'draft';
  subject?: string;
  text_preview?: string;
  content: string;
  audience_id: number;
  from_name?: string;
  from_address?: string;
  scheduled_type: 'now' | 'later' | 'recurring';
  scheduled_recurring_type?: 'recurring_weekly' | 'recurring_monthly' | 'recurring_biannually' | 'recurring_annually' | 'recurring_birthday';
  scheduled_at?: string;
  published_at?: string;
  created_at?: string;
  updated_at?: string;
}

export interface IAudience {
  id: number;
  title: string;
  imported_customer_ids?: number[];
  removed_customer_ids?: number[];
  included_customers_count?: number;
  filters: IAudienceFilter[];
  created_at: string;
  updated_at: string;
}
export interface IAudienceFilter {
  field: string;
  operator: string;
  value: (string | number)[];
}
export interface IAudienceCustomer {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  created_at: string;
  status: 'active' | 'excluded' | 'unsubscribed';
  old_status?: IAudienceCustomer['status'];
  excluded: boolean;
  preference_marketing_email: boolean;
}

export interface IPaginatedResponse<T> {
  current_page: number;
  data: T[];
  first_page_url: string;
  from: number;
  last_page: number;
  last_page_url: string;
  links: {
    url: string | null;
    label: string;
    active: boolean;
  }[];
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number;
  total: number;
}

interface IGetAudienceCustomersResponse {
  customers: IPaginatedResponse<IAudienceCustomer>;
  excluded_count: number;
  included_count: number;
  customers_from_filters_count: number;
}

export async function GetEmailCampaigns(params: unknown, useGlobalLoader: boolean = false) {
  return await APIGet<IEmailCampaign[]>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns"), params, useGlobalLoader);
}

export async function PostEmailCampaign(params: unknown, useGlobalLoader: boolean = false) {
  return await APIPost<IEmailCampaign>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns"), params, useGlobalLoader);
}

export async function PutStep1EmailCampaign(params: IPutStep1EmailCampaign, useGlobalLoader: boolean = false) {
  return await APIPut<IEmailCampaign>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/step1"), params, useGlobalLoader);
}
export async function PutStep2EmailCampaign(params: IPutStep2EmailCampaign, useGlobalLoader: boolean = false) {
  return await APIPut<IEmailCampaign>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/step2"), params, useGlobalLoader);
}
export async function PutStep3EmailCampaign(params: IPutStep3EmailCampaign, useGlobalLoader: boolean = false) {
  return await APIPut<IEmailCampaign>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/step3"), params, useGlobalLoader);
}

export async function PutPublishEmailCampaign(params: IPutPublishEmailCampaign, useGlobalLoader: boolean = false) {
  return await APIPut<IEmailCampaign>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/publish"), params, useGlobalLoader);
}

export async function GetAudience(params: unknown, useGlobalLoader: boolean = false) {
  return await APIGet<IAudience>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/audience"), params, useGlobalLoader);
}
export async function GetAudiences(params: unknown, useGlobalLoader: boolean = false) {
  return await APIGet<IAudience[]>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/audiences"), params, useGlobalLoader);
}
export async function GetAudienceCustomers(params: IGetAudienceCustomer, useGlobalLoader: boolean = false, cancelToken?: CancelToken) {
  return await APIGet<IGetAudienceCustomersResponse>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/audience/customers"), params, useGlobalLoader, cancelToken);
}
export async function PostAudienceCustomers(params: FormData, useGlobalLoader: boolean = false) {
  return await APIPost<IAudienceCustomer[]>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/audience/customers"), params, useGlobalLoader);
}
export async function GetAudienceCustomersExport(params: IGetAudienceCustomerExport, useGlobalLoader: boolean = false) {
  return await APIGet<string>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/audience/customers/export"), params, useGlobalLoader);
}
export async function PostAudience(params: IPostAudience, useGlobalLoader: boolean = false) {
  return await APIPost<IAudience>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/audience"), params, useGlobalLoader);
}
export async function PutAudience(params: IPutAudience, useGlobalLoader: boolean = false) {
  return await APIPut<IAudience>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/audience"), params, useGlobalLoader);
}

export async function PostCampaignDuplicate(params: { id: number; }, useGlobalLoader: boolean = false) {
  return await APIPost<IEmailCampaign>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/duplicate"), params, useGlobalLoader);
}

export async function DeleteCampaign(params: { id: number; }, useGlobalLoader: boolean = false) {
  return await APIPost<IEmailCampaign>(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/delete"), params, useGlobalLoader);
}

export async function PostCustomer(params: { id: number; customer_id: number, excluded: boolean }, useGlobalLoader: boolean = false) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/marketing/email-campaigns/customer"), params, useGlobalLoader);
}

